import React, { useContext } from 'react'
import Box from '@zuto/core/src/box'
import { colors } from '@zuto/theme/src/zuto/core'
import { GridItem } from '@zuto/core/src/grid'
import { AutoTrader } from '@zuto/icons'
import styled from 'styled-components'
import { styled as MuiStyled, Typography } from '@mui/material'
import { VehicleDetailsDataObject } from '../../../services/vehicle-service'
import VehicleInfo from './VehicleInfo'
import StartApplicationButton from '../../NavigationButtons/StartApplicationButton'
import { getCTAText } from '../../Questions/utils'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import CheckIcon from '@mui/icons-material/Check'
import { muiColors } from '@zuto/theme/src/mui/DefaultTheme'
import { v4 as uuidv4 } from 'uuid'
import { convertToKebabCase } from '../../../utils/utils'

export type BaseAtLandingPageV6StyledProps = {
  $atLandingPageV6?: boolean
  $vehicleImg?: boolean
}

type StyledCenterAlignBoxProps = BaseAtLandingPageV6StyledProps

const StyledCenterAlign = styled(Box)<StyledCenterAlignBoxProps>`
    text-align: center;

    @media (max-width: 767px) {
      margin-top: ${(props) => (props.$atLandingPageV6 && props.$vehicleImg ? '0 !important' : '23px')};
    }
  
    @media (max-width: 435px) {
      font-size: 18px;
      line-height: 1.43;
    }
`

const StyledBlueBox = styled(Box)`
  display: flex;
  flex-direction: column;
  background: ${colors.material[0]};
  border-radius: 12px;
  margin-top: 0;

  h3 {
    line-height: 1.27;
  }
`

const Grid = styled.div`
  display: grid;
  margin-top: 15px;
  grid-template-columns: 1fr;
  grid-template-rows: 0.5fr 1fr;
  grid-template-areas: 
    "LogoBox"
    "VehicleInfoBox";

  @media (min-width: 360px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
    grid-template-areas: 
    "LogoBox VehicleInfoBox";
  }
`

const FullWidthLogoBox = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
`

const LogoBox = styled(Box)`
  grid-area: LogoBox;
  display: flex;
  flex-direction: column;
  justify-content: end;
  align-items: start;

  svg {
    width: 161px;
    max-width: 161px;
  }
  
  @media (min-width: 360px) {
    justify-content: center;
    align-items: start;

    svg {
      max-width: 132px;
    }
  }
  
  @media(min-width: 375px) {
    svg {
      max-width: 142px;
    }
  }
`

const VehicleInfoBox = styled(Box)`
  grid-area: VehicleInfoBox;
  display: flex;
  justify-content: start;
  align-items: start;
  margin: 1em 0;
`

const MediaQueryGrid = styled(GridItem)`

  @media (max-width: 370px) {
    
  button {
    font-size: 14px;
    span {
      margin: auto;
    }
  }
}
`

const BulletPointsWrapper = MuiStyled('div')(({ theme }) => ({
  marginBottom: theme.spacing(5),
  ul: {
    margin: theme.spacing(0),
    padding: theme.spacing(0),
    listStyle: 'none'
  },
  li: {
    display: 'flex',
    alignItems: 'center'
  },
  svg: {
    marginRight: theme.spacing(2)
  }
}))

type IconProps = {
  'name'?: string
  'data-testid': string
  'viewBox'?: string
  'width'?: number
}

const icons: { [key: string]: React.FC } = {
  AutoTrader
}

interface VehicleIntroductionProps {
  vehicle?: VehicleDetailsDataObject,
  vehicleImg: boolean,
  isClassifiedSource: boolean,
  sourceDisplayName?: string
}

const VehicleCard = ({
  vehicle,
  vehicleImg,
  isClassifiedSource,
  sourceDisplayName
}: VehicleIntroductionProps): JSX.Element => {
  const atText = getCTAText(sourceDisplayName)
  const { featureSwitches } = useContext(ApplicationFormContext)
  const {
    atLandingPageV4,
    atLandingPageV6
  } = featureSwitches ?? {}

  const LogoComponent = ({ name, ...rest }: IconProps) => {
    const IconComponent = name && icons[name]
    return IconComponent ? <IconComponent {...rest} /> : null
  }

  const classifiedSourceLogoAndVehicleInfo = (
    <>
      <Grid>
        <LogoBox>
          <LogoComponent name={sourceDisplayName} data-testid={`${sourceDisplayName?.toLowerCase()}-logo`} viewBox="0 0 151 32" />
        </LogoBox>
        <VehicleInfoBox>
          <VehicleInfo isClassifiedSource={isClassifiedSource} vehicle={vehicle as VehicleDetailsDataObject} />
        </VehicleInfoBox>
      </Grid>
    </>
  )

  const classifiedSourceLogo = (
    <FullWidthLogoBox my={5}>
      <LogoComponent name={sourceDisplayName} data-testid={`${sourceDisplayName?.toLowerCase()}-logo-only`} width={161} />
    </FullWidthLogoBox>
  )

  const classifiedSourceBelowCTA = (
    <>
      <StyledCenterAlign as="h3" my={5} textStyle="material.body.medium.bold" $atLandingPageV6={atLandingPageV6} $vehicleImg={vehicleImg}>
        {atText && atText.landingTextLine} <br />
        no impact on your credit score
      </StyledCenterAlign>
    </>
  )

  const vehicleExists: boolean = (vehicle === undefined) ? false : Object.keys(vehicle).length > 0

  const renderClassifiedSourceContent = () => (vehicleExists && vehicleImg && Boolean(vehicle?.make) && Boolean(vehicle?.model)
    ? classifiedSourceLogoAndVehicleInfo
    : classifiedSourceLogo)

  const bulletPointsData = [
    { label: "We'll work to your budget" },
    { label: "Rated excellent on Trustpilot with over 17,000 reviews" },
    { label: "£0 deposit options available" }
  ]

  const renderBulletPoints = () => {
    return (
      <BulletPointsWrapper data-testid="autotrader-bullet-points">
        <ul>
          {bulletPointsData.map((point) => (
            <li key={uuidv4()}>
              <CheckIcon
                aria-label={point.label}
                sx={{ color: muiColors.primary.darkGreen }}
                data-testid={convertToKebabCase(point.label)}
              />
              <Typography variant="body1">{point.label}</Typography>
            </li>
          ))}
        </ul>
      </BulletPointsWrapper>
    )
  }

  return (
    <MediaQueryGrid gridColumn={['auto / span 12', '3 / span 8']}>
      <StyledBlueBox px={4} mt={4}>
        {renderClassifiedSourceContent()}
        {atLandingPageV4 && renderBulletPoints()}
        <StartApplicationButton isCentered vehicleImg={vehicleImg} />
        {classifiedSourceBelowCTA}
      </StyledBlueBox>
    </MediaQueryGrid>
  )
}

export default VehicleCard
